import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Mascot } from '@redpandev/ui';
import { AmbientBackdrop } from '../../components/hub/AmbientBackdrop';
import { pillSecondary } from '../../components/hub/linkStyles';

/**
 * Date de dernière révision du CONTENU légal (pas du rendu) : à mettre à jour
 * manuellement à chaque modification de texte. Affichée sur les trois pages.
 */
export const LEGAL_LAST_UPDATED = '14 juillet 2026';

/** Les trois pages légales — réutilisé par le pied de page du hub et les liens croisés. */
export const LEGAL_LINKS = [
  { to: '/confidentialite', label: 'Confidentialité' },
  { to: '/cgu', label: 'CGU' },
  { to: '/mentions-legales', label: 'Mentions légales' },
] as const;

// Styles de prose (arbitrary variants Tailwind) : on écrit du HTML sémantique
// simple (h2/h3/p/ul/a) dans chaque page, la mise en forme est portée ici.
const PROSE =
  'space-y-4 text-ink-300 ' +
  '[&_h2]:mt-10 [&_h2]:font-display [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-ink-100 ' +
  '[&_h3]:mt-6 [&_h3]:font-display [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-ink-100 ' +
  '[&_p]:leading-relaxed ' +
  '[&_a]:text-panda-500 [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:brightness-110 ' +
  '[&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6 ' +
  '[&_strong]:text-ink-100 ' +
  '[&_.placeholder]:rounded [&_.placeholder]:bg-panda-500/15 [&_.placeholder]:px-1.5 ' +
  '[&_.placeholder]:py-0.5 [&_.placeholder]:font-medium [&_.placeholder]:text-panda-500';

/**
 * Gabarit commun des pages légales (spec 14) : PUBLIQUES (aucune session), sobres,
 * dans les tokens du design system. Titre + date de révision + prose + liens croisés.
 */
export function LegalLayout({
  title,
  current,
  intro,
  children,
}: {
  title: string;
  /** Route de la page courante — retirée des liens croisés. */
  current: string;
  intro?: ReactNode;
  children: ReactNode;
}) {
  const others = LEGAL_LINKS.filter((l) => l.to !== current);

  return (
    <main className="relative overflow-hidden">
      <AmbientBackdrop />
      <div className="relative mx-auto max-w-3xl px-6 py-14 sm:py-16">
        <div className="flex items-center gap-3">
          <Mascot mood="thinking" size={40} animated={false} />
          <span className="text-xs font-semibold uppercase tracking-widest text-ink-400">
            redpan.dev
          </span>
        </div>
        <h1 className="mt-4 font-display text-3xl font-bold text-ink-100 sm:text-4xl">{title}</h1>
        <p className="mt-2 text-sm text-ink-400">
          Dernière mise à jour&nbsp;: {LEGAL_LAST_UPDATED}
        </p>
        {intro ? <div className="mt-6 text-ink-200">{intro}</div> : null}

        <div className={`mt-8 ${PROSE}`}>{children}</div>

        <nav
          aria-label="Pages légales"
          className="mt-14 flex flex-wrap items-center gap-3 border-t border-ink-600/40 pt-8"
        >
          <Link to="/" className={pillSecondary}>
            Retour au hub
          </Link>
          {others.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="text-sm text-ink-400 underline underline-offset-2 transition-colors duration-fast ease-fun hover:text-panda-500"
            >
              {l.label}
            </Link>
          ))}
        </nav>
      </div>
    </main>
  );
}

/** Placeholder visible pour un fait à compléter par un humain (identité, hébergeur…). */
export function Todo({ children }: { children: ReactNode }) {
  return <span className="placeholder">[À COMPLÉTER&nbsp;: {children}]</span>;
}
